/** Synthetic tab id for a teammate dossier (`/people/:userId`). */
export const PERSON_VIEW_TAB_PREFIX = 'person-view-' as const
export const PEOPLE_INDEX_TAB_ID = 'people-index' as const

export function isPeopleIndexTabId(tabId: string | null | undefined): boolean {
  return tabId === PEOPLE_INDEX_TAB_ID
}

export function personViewTabId(userId: string): string {
  return `${PERSON_VIEW_TAB_PREFIX}${userId}`
}

export function isPersonViewTabId(tabId: string | null | undefined): boolean {
  return Boolean(tabId?.startsWith(PERSON_VIEW_TAB_PREFIX))
}

export function isPersonSurfaceTabId(tabId: string | null | undefined): boolean {
  return isPeopleIndexTabId(tabId) || isPersonViewTabId(tabId)
}

/** People crumb matches Back when the previous slot entry is the roster. */
export function peopleIndexIsPreviousEntry(
  history: Array<{ documentId: string | null }> | undefined,
  currentIndex: number,
): boolean {
  if (!history || currentIndex <= 0) return false
  return history[currentIndex - 1]?.documentId === PEOPLE_INDEX_TAB_ID
}

export function dispatchOpenPeopleIndex(workspaceId?: string) {
  window.dispatchEvent(
    new CustomEvent('flownotes:open-people-index', {
      detail: { workspaceId },
    }),
  )
}

type PeopleSurfaceTab = {
  id: string
  type?: string
  personUserId?: string
  title?: string
  workspaceId?: string | null
}

/** One People surface tab. Going up must not leave a second `people-index`. */
export function reduceOpenTabsToPeopleIndex<T extends PeopleSurfaceTab>(
  tabs: T[],
  activeTabId: string | null,
): T[] {
  const surface = tabs.filter((tab) => tab.type === 'person-view')
  if (surface.length === 0) return tabs
  const rest = tabs.filter((tab) => tab.type !== 'person-view')
  const current = tabs.find((tab) => tab.id === activeTabId)
  const source =
    current?.type === 'person-view'
      ? current
      : surface.find((tab) => tab.id === PEOPLE_INDEX_TAB_ID) || surface[0]
  const { personUserId: _drop, ...indexTab } = {
    ...source,
    id: PEOPLE_INDEX_TAB_ID,
    title: 'People',
  }
  return [...rest, indexTab as T]
}

type PeopleIndexPlaceTab = PeopleSurfaceTab & {
  isPinned?: boolean
  tabSlotId?: string
}

/**
 * Open People in the active unpinned tab. A pinned tab, Home, or Explorer stays
 * put and People is added beside it. An existing People tab is reused.
 */
export function openPeopleIndexInTabs<T extends PeopleIndexPlaceTab>(
  tabs: T[],
  activeTabId: string | null,
  peopleTab: T,
): { tabs: T[]; slotId: string; replacedTabId: string | null } {
  const reduced = reduceOpenTabsToPeopleIndex(tabs, activeTabId)
  const existing = reduced.find((tab) => tab.id === PEOPLE_INDEX_TAB_ID)
  if (existing) {
    return {
      tabs: reduced.map((tab) =>
        tab.id === PEOPLE_INDEX_TAB_ID
          ? { ...tab, workspaceId: peopleTab.workspaceId ?? tab.workspaceId }
          : tab,
      ),
      slotId: existing.tabSlotId || peopleTab.tabSlotId || 'tab-main',
      replacedTabId: null,
    }
  }

  const current = tabs.find((tab) => tab.id === activeTabId)
  const canReplace =
    !!current &&
    !current.isPinned &&
    current.type !== 'home' &&
    current.id !== 'list' &&
    current.id !== PEOPLE_INDEX_TAB_ID

  if (canReplace && current) {
    const slotId = current.tabSlotId || peopleTab.tabSlotId || 'tab-main'
    return {
      tabs: reduced.map((tab) =>
        tab.id === current.id ? { ...peopleTab, tabSlotId: slotId } : tab,
      ),
      slotId,
      replacedTabId: current.id,
    }
  }

  const slotId = peopleTab.tabSlotId || 'tab-main'
  return {
    tabs: [...reduced, { ...peopleTab, tabSlotId: slotId }],
    slotId,
    replacedTabId: null,
  }
}

/** Drill People index into a person without leaving a leftover index tab. */
export function drillOpenTabToPerson<T extends PeopleSurfaceTab>(
  tabs: T[],
  activeTabId: string | null,
  person: { tabId: string; userId: string; title: string },
): T[] {
  const current = tabs.find((tab) => tab.id === activeTabId)
  const source =
    current?.type === 'person-view'
      ? current
      : tabs.find((tab) => tab.id === PEOPLE_INDEX_TAB_ID)
  if (!source) return tabs
  return tabs
    .filter((tab) => tab.type !== 'person-view' || tab.id === source.id)
    .map((tab) =>
      tab.id === source.id
        ? { ...tab, id: person.tabId, personUserId: person.userId, title: person.title }
        : tab,
    )
}

export function personUserIdFromTabId(tabId: string | null | undefined): string | null {
  if (!tabId?.startsWith(PERSON_VIEW_TAB_PREFIX)) return null
  const id = tabId.slice(PERSON_VIEW_TAB_PREFIX.length)
  return id || null
}

export type OpenPersonViewDetail = {
  userId: string
  title?: string
  workspaceId?: string
}

/** Open or focus a person dossier tab inside the shell. */
export function dispatchOpenPersonView(userId: string, title?: string, workspaceId?: string) {
  window.dispatchEvent(
    new CustomEvent('flownotes:open-person-view', {
      detail: { userId, title, workspaceId } satisfies OpenPersonViewDetail,
    }),
  )
}

export function personViewTabTitle(name: string): string {
  const trimmed = name.trim() || 'Person'
  return trimmed.replace(/^People\s*\/\s*/, '') || 'Person'
}

export function resolvePersonWorkspaceId(
  preferred: string | null | undefined,
  currentTabWorkspaceId: string | null | undefined,
  activeWorkspaceIds: string[],
): string | undefined {
  if (preferred && activeWorkspaceIds.includes(preferred)) return preferred
  if (currentTabWorkspaceId && activeWorkspaceIds.includes(currentTabWorkspaceId)) {
    return currentTabWorkspaceId
  }
  return activeWorkspaceIds[0]
}

/** Role label for the workspace this People index is scoped to. */
export function personWorkspaceRole(
  workspaces: Array<{ organization_id: string; role?: string | null }> | null | undefined,
  workspaceId: string | null | undefined,
): string | null {
  if (!workspaceId || !workspaces?.length) return null
  const role = workspaces.find((row) => row.organization_id === workspaceId)?.role?.trim()
  if (!role) return null
  const words = role.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Created stays distinct. Every other edit reads as Edited. */
export function personActivityVerb(actionType: string | null | undefined): 'Created' | 'Edited' {
  return actionType === 'created' ? 'Created' : 'Edited'
}

/** One-line peek copy. The panel splits the time onto a second line so it is not clipped. */
export function personActivityLine(
  actionType: string | null | undefined,
  title: string | null | undefined,
  when: string | null | undefined,
): string {
  const verb = personActivityVerb(actionType)
  const name = title?.trim() || 'Untitled'
  const time = when?.trim()
  return time ? `${verb} ${name} - ${time}` : `${verb} ${name}`
}

export function personInitials(name: string | null | undefined, email: string | null | undefined): string {
  const source = (name || email || '?').trim()
  const parts = source.split(/[\s@._-]+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  return source.slice(0, 2).toUpperCase()
}

export function formatPersonRelativeTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  const sec = Math.floor((Date.now() - d.getTime()) / 1000)
  if (sec < 60) return 'just now'
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`
  if (sec < 604800) return `${Math.floor(sec / 86400)}d ago`
  return d.toLocaleDateString()
}

export function dispatchOpenAllEditsByPerson(userId: string) {
  window.dispatchEvent(
    new CustomEvent('flownotes:open-search-view', {
      detail: { view: 'activity', user: userId },
    }),
  )
}

export type PeopleAssignment = {
  id: string
  title: string
  content?: string
  url?: string | null
  app_url?: string | null
  _app_url?: string | null
  due_on?: string | null
  completed?: boolean
  project_id?: string | null
  bucket_id?: string | null
  project_name?: string | null
  project?: { id?: string | null; name?: string | null } | null
}

export function assignmentOpenTarget(assignment: PeopleAssignment): {
  todoId: string
  projectId: string
  url: string | null
} | null {
  const todoId = String(assignment.id || '').trim()
  const projectId = String(
    assignment.project_id || assignment.bucket_id || assignment.project?.id || '',
  ).trim()
  const url = (assignment.app_url || assignment.url || assignment._app_url || '').trim() || null
  if (!todoId && !url) return null
  return { todoId, projectId, url }
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

/** Org ids for People list fetches. Empty means do not query (no active scope). */
export function personListOrgIds(
  scope: string[] | null,
  activeWorkspaceIds: string[],
): string[] {
  const raw = scope ?? activeWorkspaceIds
  return raw.filter((id) => UUID_RE.test(id))
}

export function personListOrgQuery(orgIds: string[]): string {
  if (orgIds.length === 0) return ''
  return `&organization_ids=${encodeURIComponent(orgIds.join(','))}`
}

export function isPersonListSessionPath(filePath: string | null | undefined): boolean {
  return Boolean(filePath && /(^|\/)system\/sessions\//.test(filePath))
}

export function formatAssignmentDue(dueOn: string | null | undefined): string {
  if (!dueOn) return ''
  const ymd = String(dueOn).slice(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd
  return formatPersonRelativeTime(dueOn)
}
